import { Action, PayloadAction } from '@reduxjs/toolkit';
import { Epic, combineEpics } from 'redux-observable';
import { RootState } from 'store/wos_store';
import { defer, filter, from, map, mergeMap, retry, takeUntil, timer, withLatestFrom } from 'rxjs';

import { catchAndLogError } from 'utils/common';

import canvases from 'domains/canvases';

import Arrangement from 'domains/arrangement';

import module from './index';
import utils from './saved_arrangements.utils';
import * as api from './saved_arrangements.api';

import type { SavedArrangement } from './saved_arrangements.types';

import { selectCartPhotos } from 'selectors/cart_photos';

const { actions, selectors } = module;

function collectArrangementData(state: RootState, includePhotos: boolean) {
  const withProducts = canvases.selectors.allWithProducts(state);
  const arrangement = Arrangement.createWithProducts(withProducts);

  return {
    height: arrangement.height,
    width: arrangement.width,
    canvases: selectors.forSave(state),
    crops: includePhotos ? selectCartPhotos(state).map(utils.formatCropForSave) : [],
  };
}

const getAllArrangementsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.getAll.pending.match),
    mergeMap(() =>
      from(api.all()).pipe(
        map((res) => actions.getAll.fulfilled(res)),
        catchAndLogError(actions.getAll.rejected)
      )
    )
  );

const createArrangementEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.create.pending.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      return {
        name: payload.name,
        ...collectArrangementData(state, payload.includePhotos),
      };
    }),
    mergeMap((payload) =>
      from(api.create(payload)).pipe(
        map((res) => actions.create.fulfilled(res)),
        catchAndLogError(actions.create.rejected)
      )
    )
  );

const updateArrangementEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.update.pending.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      return {
        ...payload,
        name: payload.name,
        id: payload.id,
        ...collectArrangementData(state, payload.includePhotos),
      };
    }),
    mergeMap((payload) =>
      from(api.update(payload.id, payload)).pipe(
        map((res) => actions.update.fulfilled(res)),
        catchAndLogError(actions.update.rejected)
      )
    )
  );

const refreshArrangementEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(
      (action): action is PayloadAction<SavedArrangement> =>
        actions.create.fulfilled.match(action) || actions.update.fulfilled.match(action)
    ),
    map(({ payload }) => payload.id),
    mergeMap((id) =>
      // create observable for request, use defer to re-run the promise when retried
      defer(() => api.find(id)).pipe(
        retry({
          count: 5,
          delay: (error, retries) =>
            timer(5000 * retries).pipe(
              // Cancel the retries if this arrangement is removed
              takeUntil(
                action$.pipe(
                  filter(actions.destroy.pending.match),
                  filter(({ payload }) => payload === id)
                )
              )
            ),
        }),
        map((arrangement) => actions.updateOne({ id: arrangement.id, changes: arrangement })),
        catchAndLogError()
      )
    )
  );

const deleteArrangementEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.destroy.pending.match),
    mergeMap(({ payload }) =>
      from(api.destroy(payload)).pipe(
        map(() => actions.destroy.fulfilled(payload)),
        catchAndLogError(actions.destroy.rejected)
      )
    )
  );

const combined = combineEpics(
  getAllArrangementsEpic,
  createArrangementEpic,
  updateArrangementEpic,
  refreshArrangementEpic,
  deleteArrangementEpic
);
export default combined;
